'use client';

import { c as _c } from "react/compiler-runtime";
import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import { TrackableDiscoveryItem } from '@udemy-v2/react-browse-event-tracking';
import { BrowseCourse } from '@udemy-v2/browse-course';
import { isBrowseCourse, useFetchBundleFromCourse, type CertificationBundle, ProductCardCertificationVoucher } from '@udemy-v2/react-certification-preparation';
import { generateTrackingId } from '@udemy-v2/event-tracking';
import { useAvailableExamAndVoucherForTopics } from '@udemy-v2/react-certification-vouchers';
import { useClpCourse } from '@/features/course-landing-page/context/clp-course-provider.react-component';
export interface VoucherContext {
  // Whether or not we are still loading voucher-related data.
  isLoading: boolean;
  // The certification voucher we want to display for the course.
  certificationVoucher: TrackableDiscoveryItem<ProductCardCertificationVoucher> | null;
  certificationTopicId: string | null;
  // A voucher-related bundle of courses, if any.
  bundledCertificationCourses: TrackableDiscoveryItem<BrowseCourse>[];
  // All courses in the certification bundle (including the current course), ordered
  // as [main course, practice exam course]. Used by CertificationVoucherBundleUnit.
  // Bundle `contents` is a `Buyable[]` union: full BrowseCourse cards in production,
  // CourseBuyable stubs in some hardcoded fallback paths.
  hydratedCertificationBundle: CertificationBundle | null;
}
export const ReactCLPVoucherContext = createContext<VoucherContext | undefined>(undefined);
export interface CLPVoucherContextProviderProps {
  children: ReactNode;
}
export const CLPVoucherContextProvider: React.FC<CLPVoucherContextProviderProps> = props => {
  const $ = _c(20);
  const course = useClpCourse();
  let voucherTopicIds;
  if ($[0] !== course.primaryTopic || $[1] !== course.topics) {
    voucherTopicIds = [];
    if (course?.primaryTopic?.id != null) {
      voucherTopicIds.push(String(course.primaryTopic.id));
    }
    course?.topics?.forEach(topic => {
      const id = String(topic.id);
      if (!voucherTopicIds.includes(id)) {
        voucherTopicIds.push(id);
      }
    });
    $[0] = course.primaryTopic;
    $[1] = course.topics;
    $[2] = voucherTopicIds;
  } else {
    voucherTopicIds = $[2];
  }
  const {
    isLoading: isLoadingVouchers,
    exam,
    selectedVoucherPool
  } = useAvailableExamAndVoucherForTopics(voucherTopicIds);
  const t0 = !!course.id;
  let t1;
  if ($[3] !== t0) {
    t1 = {
      enabled: t0
    };
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const {
    bundle,
    isLoading: loadingBundle
  } = useFetchBundleFromCourse(course.id, t1);
  const hydratedCertificationBundle = bundle ?? null;
  let t2;
  bb0: {
    if (!hydratedCertificationBundle) {
      let t3;
      if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = [];
        $[5] = t3;
      } else {
        t3 = $[5];
      }
      t2 = t3;
      break bb0;
    }
    let t3;
    if ($[6] !== course.isPracticeTestCourse || $[7] !== hydratedCertificationBundle.contents) {
      let t4;
      if ($[9] !== course.isPracticeTestCourse) {
        t4 = c => c.isPracticeTestCourse !== course.isPracticeTestCourse;
        $[9] = course.isPracticeTestCourse;
        $[10] = t4;
      } else {
        t4 = $[10];
      }
      t3 = hydratedCertificationBundle.contents.filter(isBrowseCourse).filter(t4).map(_temp);
      $[6] = course.isPracticeTestCourse;
      $[7] = hydratedCertificationBundle.contents;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    t2 = t3;
  }
  const bundledCertificationCourses = t2;
  const t3 = isLoadingVouchers || loadingBundle;
  const t4 = exam?.topicId ?? null;
  let t5;
  if ($[11] !== bundledCertificationCourses || $[12] !== hydratedCertificationBundle || $[13] !== selectedVoucherPool || $[14] !== t3 || $[15] !== t4) {
    t5 = {
      isLoading: t3,
      certificationTopicId: t4,
      certificationVoucher: selectedVoucherPool,
      bundledCertificationCourses,
      hydratedCertificationBundle
    };
    $[11] = bundledCertificationCourses;
    $[12] = hydratedCertificationBundle;
    $[13] = selectedVoucherPool;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== props.children || $[18] !== t5) {
    t6 = <ReactCLPVoucherContext.Provider value={t5}>{props.children}</ReactCLPVoucherContext.Provider>;
    $[17] = props.children;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
};

/**
 * Custom hook to access the CLP purchase context
 * @returns CLPVoucherContext
 * @throws Error if used outside of CLPVoucherContextProvider
 */
export function useClpVoucherContext() {
  const context = useContext(ReactCLPVoucherContext);
  if (context === undefined) {
    throw new Error("useClpVoucherContext must be used within a CLPVoucherContextProvider");
  }
  return context;
}
function _temp(c_0) {
  return {
    ...c_0,
    frontendTrackingId: generateTrackingId()
  };
}